<template>
  <b-modal
    id="modal-media"
    :modal-class="{
      'mc-vehicle-media__modal-media': true,
      'single-media': !groupingBasedOnDate,
    }"
    scrollable
    size="xl"
    centered
    @show="showModal"
    @hide="onCloseModal"
  >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center text-white">
        <span>
          {{ modalTitle }}
        </span>
        <i class="icon-outline_close" @click="close()" />
      </div>
    </template>
    <div class="mc-vehicle-media__modal-media-content">
      <div class="mc-vehicle-media__information-wrapper">
        <h3>{{ $t('global.object.info') }}</h3>
        <div class="mc-vehicle-media__information-list">
          <div>
            <p class="mc-vehicle-media__information-label">{{ $t('global.field.savedDate') }}</p>
            <p class="mc-vehicle-media__information-content">
              {{ dateSaved }}
            </p>
          </div>
          <div>
            <p class="mc-vehicle-media__information-label">
              {{ $t('global.field.fleetLocation') }}
            </p>

            <p class="mc-vehicle-media__information-content">
              {{ modalMedia?.address }}
            </p>
          </div>
          <div>
            <p class="mc-vehicle-media__information-label">
              {{ $t('global.driver') }}
            </p>

            <p class="mc-vehicle-media__information-content">
              {{ driverName }}
            </p>
          </div>
          <div>
            <p class="mc-vehicle-media__information-label">
              {{ $t('global.object.fleetGroup') }}
            </p>
            <p
              v-b-tooltip.hover.right="vgroupsList.map((g) => g.vgroup.name).join(', ')"
              class="mc-vehicle-media__information-content"
            >
              {{ vgroupsFormatted }}
            </p>
          </div>
          <div>
            <p class="mc-vehicle-media__information-label">
              {{ $t('global.object.fleetType') }}
            </p>
            <p class="mc-vehicle-media__information-content">
              {{ vehicleType }}
            </p>
          </div>
        </div>
      </div>

      <!-- for if theres is media video and picture -->
      <b-tabs
        v-if="groupingBasedOnDate && listMediaPicture?.length && listMediaVideo?.length"
        content-class="p-3 bg-white"
        nav-wrapper-class="mc-tab"
      >
        <b-tab :title="$t('global.photo')" active>
          <div
            class="mc-vehicle-media__media-photo-wrapper"
            :class="{ 'single-media': listMediaPicture.length === 1 }"
          >
            <MediaPhoto
              v-for="(media, index) in listMediaPicture"
              :key="media.downloadId + '-' + index"
              :camera-number="media.calculatedChannel"
              :src="media.previewUrl || media.url"
              :download-id="media.downloadId"
              :media-filename="media.mediaFilename"
              :loading-generate-picture="
                loadingGeneratedImageMap[
                  `${media.vehicle?.id}-${media.downloadId}-${media.calculatedChannel}`
                ] || false
              "
            />
          </div>
        </b-tab>
        <b-tab :title="'Video'">
          <div
            class="mc-vehicle-media__media-video-wrapper"
            :class="{ 'single-media': listMediaVideo?.length === 1 }"
          >
            <MediaVideo
              v-for="(media, index) in listMediaVideo"
              :key="media.downloadId + '-' + index"
              :cameraNumber="media.calculatedChannel"
              :src="media.url"
              :download-id="media.downloadId"
            />
          </div>
        </b-tab>
      </b-tabs>

      <!-- for if theres is media video only or media picture only -->
      <template v-else>
        <div
          v-if="groupingBasedOnDate && listMediaPicture?.length"
          class="p-3 bg-white mc-vehicle-media__modal-media-content-wrapper"
        >
          <div
            class="mc-vehicle-media__media-photo-wrapper"
            :class="{ 'single-media': listMediaPicture.length === 1 }"
          >
            <MediaPhoto
              v-for="(media, index) in listMediaPicture"
              :key="media.downloadId + '-' + index"
              :camera-number="media.calculatedChannel"
              :src="media.previewUrl || media.url"
              :download-id="media.downloadId"
              :media-filename="media.mediaFilename"
              :loading-generate-picture="
                loadingGeneratedImageMap[
                  `${media.vehicle?.id}-${media.downloadId}-${media.calculatedChannel}`
                ] || false
              "
            />
          </div>
        </div>
        <div
          v-else-if="groupingBasedOnDate && listMediaVideo?.length"
          class="p-3 bg-white mc-vehicle-media__modal-media-content-wrapper"
        >
          <div
            class="mc-vehicle-media__media-video-wrapper"
            :class="{ 'single-media': listMediaVideo.length === 1 }"
          >
            <MediaVideo
              v-for="(media, index) in listMediaVideo"
              :key="media.downloadId + '-' + index"
              :cameraNumber="media.calculatedChannel"
              :src="media.url"
              :download-id="media.downloadId"
              :autoPlay="listMediaVideo.length === 1"
            />
          </div>
        </div>
        <!-- for not grouping -->
        <template v-else>
          <div
            v-if="isPicture"
            class="p-3 bg-white mc-vehicle-media__media-photo-item-wrapper mc-vehicle-media__single-wrapper"
          >
            <MediaPhoto
              :src="singleMediaPhotoSrc"
              :cameraNumber="singleMediaPhoto.calculatedChannel"
              :download-id="singleMediaPhoto.downloadId"
              :media-filename="singleMediaPhoto.mediaFilename"
              :loading-generate-picture="
                loadingGeneratedImageMap[
                  `${singleMediaPhoto.vehicle?.id}-${singleMediaPhoto.downloadId}-${singleMediaPhoto.calculatedChannel}`
                ] || false
              "
            />
          </div>
          <div
            v-if="isVideo"
            class="p-3 bg-white mc-vehicle-media__media-video-item-wrapper mc-vehicle-media__single-wrapper"
          >
            <MediaVideo
              :src="modalMedia.url"
              :cameraNumber="modalMedia.calculatedChannel"
              :download-id="modalMedia.downloadId"
              :autoPlay="true"
            />
          </div>
        </template>
      </template>
    </div>

    <template #modal-footer="{ cancel }">
      <div
        class="d-flex w-100"
        :class="{ 'justify-content-end': !isAlert, 'justify-content-between': isAlert }"
      >
        <McButton
          v-if="isAlert"
          variant="secondary"
          :text="$t('notification.title.notificationDetail')"
          @click="openNotification"
        />
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.action.close')"
            @click="cancel()"
          />
          <McButton
            :text="
              loadingDownload
                ? $t('global.processing')
                : isMultipleMedia
                ? $t('global.action.downloadAll')
                : $t('global.action.download')
            "
            :loading="loadingDownload"
            :icon="loadingDownload ? undefined : 'icon-outline_download'"
            @click="handleClickDownload"
          />
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import MediaPhoto from './MediaPhoto.vue'
import MediaVideo from './MediaVideo.vue'
import { mapState } from 'vuex'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import moment from 'moment'
moment.locale('id')

export default {
  name: 'ModalMedia',
  components: {
    McButton,
    MediaPhoto,
    MediaVideo,
  },
  mixins: [Helper],
  data() {
    return {
      loadingDownload: false,
      calculatedMediaList: [],
      loadingGeneratedImageMap: {}, // key is ${vehicleId}-${downloadId}-${channel}
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapState('media', ['defaultCameraThumbnail', 'groupingBasedOnDate', 'modalMedia']),
    isAlert() {
      return this.modalMedia.rawAlerts.length
    },
    modalTitle() {
      let type
      const lang = this.$i18n.locale
      if (this.calculatedMediaList.length === 0) return null
      if (!this.groupingBasedOnDate) {
        if (this.modalMedia.identifier === 'H') type = 'Download Riwayat Video'
        else {
          type = this.modalMedia.rawAlerts.length
            ? lang === 'en'
              ? this.modalMedia.rawAlerts[0].alert?.descriptionEng
              : this.modalMedia.rawAlerts[0].alert?.descriptionIdn
            : 'Auto Captured'
        }
        return this.modalMedia.vehicle.parentVehicle
          ? this.modalMedia.vehicle.parentVehicle?.licensePlate
          : this.modalMedia.vehicle.licensePlate + ' - ' + type
      }
      const mapKey = {
        autoCaptured: 'Auto Captured',
        videoHistory: 'Download Riwayat Video',
        rawAlerts:
          lang === 'en'
            ? this.modalMedia.rawAlerts[0]?.alert?.descriptionEng
            : this.modalMedia.rawAlerts[0]?.alert?.descriptionIdn,
      }
      const licensePlate = this.calculatedMediaList?.[0]?.vehicle.parentVehicle
        ? this.calculatedMediaList?.[0]?.vehicle.parentVehicle?.licensePlate
        : this.calculatedMediaList?.[0]?.vehicle.licensePlate
      return licensePlate + ' - ' + mapKey[this.modalMedia.type]
    },
    isVideo() {
      if (this.calculatedMediaList.length === 0) return false
      if (this.groupingBasedOnDate) return false
      return (
        this.modalMedia?.identifier === 'V' ||
        this.modalMedia?.identifier === 'H' ||
        this.modalMedia?.identifier === 'R'
      )
    },
    isPicture() {
      if (this.calculatedMediaList.length === 0) return false
      if (this.groupingBasedOnDate) return false
      return this.modalMedia?.identifier === 'P'
    },
    listMediaPicture() {
      if (this.calculatedMediaList.length === 0) return []
      if (!this.groupingBasedOnDate) return []
      return this.calculatedMediaList?.filter((media) => media.identifier === 'P')
    },
    listMediaVideo() {
      if (this.calculatedMediaList.length === 0) return []
      if (!this.groupingBasedOnDate) return []
      return this.calculatedMediaList?.filter(
        (media) => media.identifier === 'V' || media.identifier === 'H' || media.identifier === 'R'
      )
    },
    isMultipleMedia() {
      if (this.calculatedMediaList.length === 0) return false
      if (!this.groupingBasedOnDate) return false
      return this.calculatedMediaList.length > 1
    },
    singleMediaPhoto() {
      return this.calculatedMediaList[0] || this.modalMedia
    },
    singleMediaPhotoSrc() {
      return this.singleMediaPhoto?.previewUrl || this.singleMediaPhoto?.url
    },
    dateSaved() {
      if (this.calculatedMediaList.length === 0) return null
      if (!this.groupingBasedOnDate) {
        const tz = this.modalMedia?.vehicle.device.timezone ?? 7
        return (
          this.momentDatetimeShortMonth(
            this.convertUtcToLocalDate(this.modalMedia?.startTime, tz)
          ) +
          ' ' +
          this.formatLabelTimezone(tz)
        )
      }
      const tz = this.calculatedMediaList?.[0]?.vehicle.device.timezone
      return (
        this.momentDatetimeShortMonth(
          this.convertUtcToLocalDate(this.calculatedMediaList?.[0]?.startTime, tz)
        ) +
        ' ' +
        this.formatLabelTimezone(tz)
      )
    },
    vehicleType() {
      if (this.calculatedMediaList.length === 0) return null
      let category, type, manufacturer

      if (!this.groupingBasedOnDate) {
        category = this.modalMedia.vehicle.vehicleType.category
        type = this.modalMedia.vehicle.vehicleType.type
        manufacturer = this.modalMedia.vehicle.vehicleType.manufacturer
      } else {
        category = this.calculatedMediaList[0].vehicle.vehicleType.category
        type = this.calculatedMediaList[0].vehicle.vehicleType.type
        manufacturer = this.calculatedMediaList[0].vehicle.vehicleType.manufacturer
      }
      return (category ?? '-') + ' / ' + (type ?? '-') + ' / ' + (manufacturer ?? '-')
    },
    driverName() {
      if (this.calculatedMediaList.length === 0) return null
      if (!this.groupingBasedOnDate) {
        return this.modalMedia?.vehicle?.parentVehicle
          ? this.modalMedia?.vehicle?.parentVehicle?.driver1?.fullname ?? '-'
          : this.modalMedia?.vehicle?.driver1?.fullname ?? '-'
      }
      return this.calculatedMediaList?.[0]?.vehicle?.parentVehicle
        ? this.calculatedMediaList?.[0]?.vehicle?.parentVehicle?.driver1?.fullname ?? '-'
        : this.calculatedMediaList?.[0]?.vehicle?.driver1?.fullname ?? '-'
    },
    vgroupsList() {
      if (this.calculatedMediaList.length === 0) return []
      let vgroupList
      if (!this.groupingBasedOnDate) {
        vgroupList = this.modalMedia?.vehicle?.vehicle2Vgroup ?? []
      } else {
        vgroupList = this.calculatedMediaList?.[0]?.vehicle?.vehicle2Vgroup ?? []
      }
      return vgroupList
    },
    vgroupsFormatted() {
      if (this.calculatedMediaList.length === 0) return null
      let vgroupList = this.vgroupsList
      if (vgroupList?.length === 0) return '-'
      if (vgroupList.length === 1) return vgroupList[0]?.vgroup?.name ?? '-'
      return (
        (vgroupList[0]?.vgroup?.name ?? '-') + ', +' + (vgroupList.length - 1) + ' grup lainnnya'
      )
    },

    hasScopeNewModalMedia() {
      return this.hasAccess('b3t-trackvision-new-modal-media-alert:read')
    },
  },

  methods: {
    onCloseModal() {
      this.loadingGeneratedImageMap = {}
      this.calculatedMediaList = []
      this.$bvModal.hide('modal-media')
    },
    showModal() {
      if (this.hasScopeNewModalMedia) {
        this.setPhotoFromVideoMediaList()
      } else {
        this.calculatedMediaList = this.groupingBasedOnDate
          ? this.modalMedia?.mediaList
          : [this.modalMedia]
      }
    },

    async setPhotoFromVideoMediaList() {
      const mediaList = this.groupingBasedOnDate ? this.modalMedia?.mediaList : [this.modalMedia]

      this.calculatedMediaList = mediaList

      this.calculatedMediaList = await Promise.all(
        mediaList.map(async (media) => {
          if (!media.isPictureFromVideo || media.previewUrl) return media

          let previewUrl = null

          this.$set(
            this.loadingGeneratedImageMap,
            `${media.vehicle?.id}-${media.downloadId}-${media.calculatedChannel}`,
            true
          )

          try {
            previewUrl = await this.generatePictureUrlFromVideo(media.url, {
              frame: 'middle',
              fetchAsBlob: true,
            })
          } catch (error) {
            console.error(error)
          } finally {
            this.$set(
              this.loadingGeneratedImageMap,
              `${media.vehicle?.id}-${media.downloadId}-${media.calculatedChannel}`,
              false
            )
          }

          if (!previewUrl) return media

          const arrMediaFilename = media.mediaFilename.split('.')
          arrMediaFilename[arrMediaFilename.length - 1] = 'png'

          return {
            ...media,
            previewUrl,
            mediaFilename: arrMediaFilename.join('.'),
          }
        })
      )
    },
    doDownload(url, mediaFilename) {
      return new Promise((resolve) => {
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', mediaFilename)
        link.click()
        setTimeout(() => {
          link.remove()
          resolve()
        }, 100)
      })
    },
    async doMultipleDownload(fileList) {
      for (let i = 0; i < fileList.length; i++) {
        await this.delay(250)
        await this.doDownload(fileList[i].url, fileList[i].mediaFilename)
      }
    },
    async processingConvertAndDownload(media) {
      this.loadingDownload = true
      try {
        const { downloadUrl, filename } = await this.convertVideoAndGetFile(media.url)
        this.doDownload(downloadUrl, filename ?? media.mediaFilename)
      } catch (error) {
        this.$mcToast.error('Failed convert video')
        console.error(error)
      } finally {
        this.loadingDownload = false
      }
    },
    processingSingleDownload(url, mediaFilename, identifier) {
      const hasScopeConvertVideo = this.hasAccess(['b3q-trackvision-convert-video-download:read'])
      if (
        hasScopeConvertVideo &&
        (identifier === 'V' || identifier === 'R' || identifier === 'H')
      ) {
        this.processingConvertAndDownload({ url, mediaFilename })
      } else {
        this.doDownload(url, mediaFilename)
      }
    },
    async processingMultipleDownload(mediaList) {
      const hasScopeConvertVideo = this.hasAccess(['b3q-trackvision-convert-video-download:read'])
      const isAllPicture = mediaList.every((media) => media.identifier === 'P')
      if (isAllPicture || !hasScopeConvertVideo) {
        this.doMultipleDownload(
          mediaList.map((item) => ({ url: item.url, mediaFilename: item.mediaFilename }))
        )
      } else {
        this.loadingDownload = true
        try {
          const mediaVideo = this.listMediaVideo
          const mediaPicture = this.listMediaPicture
          const res = await Promise.all(
            mediaVideo.map((item) => this.convertVideoAndGetFile(item.url))
          )
          const finalUrls = [
            ...res.map((item, index) => ({
              url: item.downloadUrl,
              mediaFilename: item.filename ?? mediaVideo[index].mediaFilename,
            })),
            ...mediaPicture.map((item) => ({
              url: item.previewUrl || item.url,
              mediaFilename: item.mediaFilename,
            })),
          ]
          this.doMultipleDownload(finalUrls)
        } catch (error) {
          this.$mcToast.error('Failed convert video')
          console.error(error)
        } finally {
          this.loadingDownload = false
        }
      }
    },
    async handleClickDownload() {
      if (!this.groupingBasedOnDate) {
        const media = this.calculatedMediaList[0]
        const url = media?.previewUrl || media.url,
          mediaFilename = media.mediaFilename,
          identifier = media.identifier
        if (url && mediaFilename) {
          this.processingSingleDownload(url, mediaFilename, identifier)
        }
      } else {
        const singleMedia = this.calculatedMediaList.length === 1
        if (singleMedia) {
          const url = this.calculatedMediaList[0].previewUrl || this.calculatedMediaList[0].url,
            mediaFilename = this.calculatedMediaList[0].mediaFilename,
            identifier = this.calculatedMediaList[0].identifier
          if (url && mediaFilename) {
            this.processingSingleDownload(url, mediaFilename, identifier)
          }
        } else {
          this.processingMultipleDownload(this.calculatedMediaList)
        }
      }
    },
    openNotification() {
      const alert = this.modalMedia.rawAlerts?.[0]

      if (!alert) return

      const url = `${window.location.protocol}//${window.location.host}/notification/detail/${alert.rawAlertId}`
      window.open(url, '_blank')
    },
  },
}
</script>
